import { Link, useParams, Navigate } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import Seo from "../components/Seo";
import Breadcrumbs from "../components/Breadcrumbs";
import CtaBanner from "../components/CtaBanner";
import { BLOG_POSTS, findPostBySlug } from "../data/blog";

export default function BlogPostPage() {
  const { slug } = useParams();
  const post = findPostBySlug(slug);
  if (!post) return <Navigate to="/blog" replace />;

  const related = BLOG_POSTS.filter((p) => p.slug !== post.slug).slice(0, 2);

  const CANONICAL_ORIGIN = "https://beautix.pl";
  const absImage = post.image.startsWith("http")
    ? post.image
    : `${CANONICAL_ORIGIN}${post.image}`;
  const pageUrl = `${CANONICAL_ORIGIN}/blog/${post.slug}`;

  const schema = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "BlogPosting",
        "@id": `${pageUrl}#article`,
        headline: post.title,
        description: post.excerpt,
        image: absImage,
        datePublished: post.date,
        dateModified: post.date,
        inLanguage: "pl-PL",
        mainEntityOfPage: { "@type": "WebPage", "@id": pageUrl },
        author: {
          "@type": "Organization",
          name: "Studio Kosmetyki Nowoczesnej BEAUTIX",
          url: CANONICAL_ORIGIN,
        },
        publisher: {
          "@type": "Organization",
          name: "BEAUTIX",
          logo: {
            "@type": "ImageObject",
            url: `${CANONICAL_ORIGIN}/assets/beautix-logo.png`,
          },
        },
        keywords: post.keywords.join(", "),
        articleSection: post.category,
      },
      {
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "Strona główna", item: `${CANONICAL_ORIGIN}/` },
          { "@type": "ListItem", position: 2, name: "Blog", item: `${CANONICAL_ORIGIN}/blog` },
          { "@type": "ListItem", position: 3, name: post.title, item: pageUrl },
        ],
      },
    ],
  };

  return (
    <>
      <Seo
        title={`${post.title} | BEAUTIX`}
        description={post.excerpt}
        keywords={post.keywords.join(", ")}
        path={`/blog/${post.slug}`}
        image={post.image}
        schema={schema}
      />

      <section className="max-w-4xl mx-auto px-6 md:px-10 pt-8 pb-4">
        <Breadcrumbs
          items={[
            { label: "Blog", to: "/blog" },
            { label: post.title },
          ]}
        />
      </section>

      <article className="max-w-4xl mx-auto px-6 md:px-10 pt-6 pb-24">
        <span className="eyebrow">{post.category}</span>
        <h1 className="font-serif-display text-4xl md:text-5xl lg:text-6xl leading-[1.05] tracking-tight mt-5">
          {post.title}
        </h1>
        <div className="flex items-center gap-4 text-sm text-[var(--beautix-muted)] mt-6">
          <span>{new Date(post.date).toLocaleDateString("pl-PL")}</span>
          <span>·</span>
          <span>{post.readMin} min czytania</span>
        </div>

        <div className="mt-12 aspect-[16/9] overflow-hidden">
          <img
            src={post.image}
            alt={post.title}
            className="w-full h-full object-cover"
          />
        </div>

        <div className="prose prose-lg max-w-none mt-14 space-y-10">
          <p className="text-xl leading-relaxed text-[var(--beautix-ink)] font-light border-l-2 border-[var(--beautix-pink)] pl-6">
            {post.excerpt}
          </p>
          {post.content.map((block) => (
            <section key={block.h}>
              <h2 className="font-serif-display text-3xl md:text-4xl leading-tight mb-4">
                {block.h}
              </h2>
              <p className="text-lg leading-relaxed text-[var(--beautix-muted)] font-light">
                {block.p}
              </p>
            </section>
          ))}
        </div>

        <div className="mt-16 border-t border-[var(--beautix-line)] pt-8 flex flex-wrap gap-2">
          {post.keywords.map((k) => (
            <span
              key={k}
              className="text-xs tracking-wide px-3 py-1.5 rounded-full bg-[var(--beautix-blush)] text-[var(--beautix-pink-dark)]"
            >
              #{k}
            </span>
          ))}
        </div>

        <Link
          to="/blog"
          className="mt-14 inline-flex items-center gap-2 text-sm font-medium link-underline"
          data-testid="back-to-blog"
        >
          <ArrowLeft className="w-4 h-4" strokeWidth={1.5} />
          Wróć do bloga
        </Link>
      </article>

      {/* Related */}
      <section className="max-w-7xl mx-auto px-6 md:px-10 pb-24">
        <p className="eyebrow mb-4">Powiązane artykuły</p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {related.map((p) => (
            <Link
              to={`/blog/${p.slug}`}
              key={p.slug}
              className="group grid grid-cols-3 gap-6 border border-[var(--beautix-line)] p-4 hover:border-[var(--beautix-pink)] transition-colors"
              data-testid={`related-${p.slug}`}
            >
              <div className="col-span-1 aspect-square overflow-hidden">
                <img src={p.image} alt={p.title} className="w-full h-full object-cover" loading="lazy" />
              </div>
              <div className="col-span-2 flex flex-col justify-center">
                <span className="eyebrow text-[10px]">{p.category}</span>
                <h3 className="font-serif-display text-xl leading-tight mt-2 group-hover:text-[var(--beautix-pink)] transition-colors">
                  {p.title}
                </h3>
              </div>
            </Link>
          ))}
        </div>
      </section>

      <CtaBanner
        title="Chcesz zaplanować wizytę na temat, o którym pisaliśmy?"
        subtitle="Zadzwoń — chętnie odpowiemy na wszystkie pytania i dobierzemy zabieg."
      />
    </>
  );
}
